/**
 * @fileoverview O layout da contratação em duas etapas (03/11/2026): a moldura
 * de altura fixa (nada pula de lugar ao mudar de etapa), as escolhas à esquerda,
 * que rolam sozinhas, e o resumo à direita, com o valor e o botão sempre à vista.
 * Abaixo de 960 px, uma coluna só, com o total e o botão fixos embaixo. No
 * celular, a tela inteira. Monocromático: o azul só no botão principal, e cor só
 * nos erros e avisos.
 */

import { makeStyles } from "@material-ui/core/styles";

import { dialogShell } from "../../../styles/dialogShell";
import { tokens } from "../../Triage/styles";

const useCheckoutStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const faint = dark ? "#8A8A93" : "#71717A";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  const strong = dark ? "rgba(255,255,255,0.6)" : "#18181B";
  const soft = dark ? "rgba(255,255,255,0.03)" : "#FAFAFA";
  const raised = dark ? "rgba(255,255,255,0.06)" : "#F4F4F5";
  const paper = dark ? "#141416" : "#FFFFFF";
  const focus = `0 0 0 1px ${dark ? "rgba(255,255,255,0.7)" : "rgba(9,9,11,0.7)"}`;
  const reduce = "@media (prefers-reduced-motion: reduce)";
  const compact = theme.breakpoints.down("sm");
  const phone = theme.breakpoints.down("xs");
  const shell = dialogShell(theme, { width: 1080 }).dialogPaper["&&"];

  const fieldBase = {
    height: 42,
    borderRadius: 10,
    border: `1px solid ${c.inputBorder}`,
    background: dark ? "rgba(255,255,255,0.04)" : "#fff",
    color: text,
    padding: "0 12px",
    fontSize: 14,
    fontFamily: "inherit",
    fontVariantNumeric: "tabular-nums",
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 120ms ease",
    "&:focus": { borderColor: dark ? "rgba(255,255,255,0.7)" : "#09090B" },
    "&::placeholder": { color: dark ? "rgba(250,250,250,0.32)" : "#A1A1AA" },
    [reduce]: { transition: "none" },
  };

  const selectBase = {
    "&& .MuiOutlinedInput-root": { height: 42, borderRadius: 10, fontSize: 14, background: dark ? "rgba(255,255,255,0.04)" : "#fff" },
    "&& .MuiOutlinedInput-notchedOutline": { borderColor: `${c.inputBorder} !important`, borderWidth: 1 },
    "&& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "none" },
    "&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: `${dark ? "rgba(255,255,255,0.7)" : "#09090B"} !important` },
  };

  return {
    // "&&": o fundo do Paper do tema (com transparência) venceria a classe empatada.
    paper: {
      "&&": {
        ...shell,
        height: "min(700px, calc(100vh - 32px))",
        maxHeight: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        [compact]: { width: "min(720px, calc(100vw - 32px))", height: "min(860px, calc(100vh - 32px))" },
        [phone]: { width: "100%", maxWidth: "100%", height: "100%", maxHeight: "100%", margin: 0, borderRadius: 0, border: "none" },
      },
    },
    // Depois de pagar (acompanhando, pago, recusado, o Pix, o boleto): um modal menor, do tamanho do conteúdo.
    paperState: { "&&": { ...shell, width: "min(640px, calc(100vw - 32px))", maxHeight: "calc(100vh - 32px)" } },
    stateScroll: { overflowY: "auto" },

    head: {
      display: "flex",
      alignItems: "center",
      gap: 20,
      padding: "16px 22px",
      borderBottom: `1px solid ${line}`,
      flexShrink: 0,
      [phone]: { flexWrap: "wrap", gap: 10, padding: "12px 16px" },
    },
    headTitle: { flex: 1, minWidth: 0, margin: 0, fontSize: 18, fontWeight: 700, color: text, [phone]: { fontSize: 17 } },
    headClose: { color: muted, [phone]: { order: 2 } },

    // As duas etapas: o número é uma sequência de verdade. A primeira volta com um clique.
    steps: { display: "flex", alignItems: "center", gap: 10, margin: 0, padding: 0, listStyle: "none", [phone]: { order: 3, flexBasis: "100%", gap: 8 } },
    stepItem: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 500, color: muted, whiteSpace: "nowrap", [phone]: { fontSize: 12.5, gap: 6 } },
    stepCurrent: { color: text, fontWeight: 650 },
    stepBack: {
      border: "none",
      background: "transparent",
      padding: 0,
      fontFamily: "inherit",
      color: text,
      cursor: "pointer",
      borderRadius: 6,
      "&:hover $stepText": { textDecoration: "underline", textUnderlineOffset: 3 },
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    stepText: { "@media (max-width: 379px)": { display: "none" } },
    stepTextCurrent: { "@media (max-width: 379px)": { display: "inline" } },
    stepDot: {
      width: 22,
      height: 22,
      borderRadius: "50%",
      border: `1px solid ${dark ? "rgba(255,255,255,0.24)" : "#A1A1AA"}`,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 650,
      fontVariantNumeric: "tabular-nums",
      flexShrink: 0,
      transition: "background 160ms ease, border-color 160ms ease, color 160ms ease",
      "& svg": { width: 13, height: 13 },
      [reduce]: { transition: "none" },
    },
    stepDotOn: { background: text, borderColor: text, color: paper },
    stepLine: { width: 28, height: 1, background: dark ? "rgba(255,255,255,0.16)" : "#D4D4D8", flexShrink: 0, [phone]: { width: 16 } },

    body: {
      flex: 1,
      minHeight: 0,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 348px",
      [compact]: { display: "flex", flexDirection: "column" },
    },
    main: {
      minHeight: 0,
      overflowY: "auto",
      padding: "24px 28px 28px",
      scrollbarWidth: "thin",
      [compact]: { flex: 1, padding: "20px 22px 24px" },
      [phone]: { padding: "16px 16px 20px" },
    },
    stepContent: {
      display: "flex",
      flexDirection: "column",
      gap: 26,
      animation: "$stepIn 200ms ease-out",
      [reduce]: { animation: "none" },
      [phone]: { gap: 22 },
    },
    section: { display: "flex", flexDirection: "column", gap: 12 },
    sectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
    sectionTitleRow: { display: "flex", alignItems: "center", gap: 4, minWidth: 0 },
    sectionTitle: { margin: 0, fontSize: 15, fontWeight: 650, color: text, outline: "none" },
    srOnly: { position: "absolute", width: 1, height: 1, margin: 0, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", outline: "none" },
    optionalTag: { marginLeft: 8, fontSize: 12.5, fontWeight: 500, color: muted },
    textLink: {
      border: "none",
      background: "transparent",
      padding: 0,
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 600,
      color: text,
      textDecoration: "underline",
      textUnderlineOffset: 3,
      cursor: "pointer",
      flexShrink: 0,
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    backLink: {
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      border: "none",
      background: "transparent",
      padding: 0,
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 500,
      color: muted,
      cursor: "pointer",
      borderRadius: 6,
      "& svg": { width: 15, height: 15 },
      "&:hover": { color: text },
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },

    // Os dois planos, com as linhas alinhadas para comparar. O marcado sobe um tom e ganha a borda forte.
    plans: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, [phone]: { gridTemplateColumns: "1fr" } },
    planCard: {
      textAlign: "left",
      fontFamily: "inherit",
      cursor: "pointer",
      border: `1px solid ${dark ? "rgba(255,255,255,0.12)" : "#D4D4D8"}`,
      borderRadius: 14,
      background: "transparent",
      color: text,
      padding: "16px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      transition: "background 140ms ease, border-color 140ms ease",
      "&:hover": { background: soft, borderColor: dark ? "rgba(255,255,255,0.24)" : "#A1A1AA" },
      "&:focus-visible": { outline: "none", boxShadow: focus },
      [reduce]: { transition: "none" },
    },
    planCardOn: {
      background: raised,
      borderColor: strong,
      "&:hover": { background: raised, borderColor: strong },
    },
    planCardOff: { cursor: "default", opacity: 0.5, "&:hover": { background: "transparent", borderColor: dark ? "rgba(255,255,255,0.12)" : "#D4D4D8" } },
    planHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
    planTitle: { fontSize: 15, fontWeight: 650 },
    planTag: { marginLeft: 8, fontSize: 12, fontWeight: 500, color: muted },
    planRadio: {
      width: 20,
      height: 20,
      borderRadius: "50%",
      border: `1.5px solid ${dark ? "rgba(255,255,255,0.32)" : "#A1A1AA"}`,
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: paper,
      transition: "background 140ms ease, border-color 140ms ease",
      "& svg": { width: 13, height: 13, animation: "$pop 160ms ease-out" },
      [reduce]: { transition: "none", "& svg": { animation: "none" } },
    },
    planRadioOn: { background: text, borderColor: text },
    planPriceRow: { display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: "0 6px" },
    planAmount: { fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", color: text },
    planUnit: { fontSize: 12.5, color: muted },
    planPerks: { display: "flex", flexDirection: "column", gap: 7, paddingTop: 12, borderTop: `1px solid ${line}` },
    planPerk: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, lineHeight: 1.4, color: muted, "& svg": { width: 15, height: 15, flexShrink: 0, color: faint } },
    planPerkStrong: { color: text, fontWeight: 600, "& svg": { color: text } },
    bothPlans: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, lineHeight: 1.45, color: muted, "& svg": { width: 15, height: 15, flexShrink: 0, marginTop: 2 } },

    // Cada escolha numa linha: o ícone, o que é e, à direita, o controle.
    rows: { display: "flex", flexDirection: "column" },
    row: {
      display: "grid",
      gridTemplateColumns: "36px minmax(0, 1fr) auto",
      alignItems: "center",
      gap: "0 12px",
      padding: "12px 0",
      borderTop: `1px solid ${line}`,
      "&:first-child": { borderTop: "none", paddingTop: 0 },
      "&:last-child": { paddingBottom: 0 },
      [phone]: { gridTemplateColumns: "minmax(0, 1fr) auto" },
    },
    rowIcon: {
      [phone]: { display: "none" },
      width: 36,
      height: 36,
      borderRadius: 10,
      background: raised,
      color: muted,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": { width: 17, height: 17 },
    },
    rowText: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    rowName: { display: "flex", alignItems: "center", gap: 4, fontSize: 14, fontWeight: 600, color: text },
    rowHint: { fontSize: 12.5, lineHeight: 1.45, color: muted, fontVariantNumeric: "tabular-nums" },
    rowControl: { display: "flex", justifyContent: "flex-end" },
    // O select não cabe ao lado do texto no celular: desce para uma linha só dele.
    rowControlWide: { [phone]: { gridColumn: "1 / -1", marginTop: 10, justifyContent: "stretch" } },
    packSelect: { ...selectBase, width: 284, [phone]: { width: "100%" } },

    // O resumo: lê como um recibo que vai se escrevendo, com o botão logo abaixo do total, no mesmo lugar nas duas etapas.
    // A linha tracejada separa o total, o maior número da tela.
    summaryPanel: {
      minHeight: 0,
      overflowY: "auto",
      scrollbarWidth: "thin",
      display: "flex",
      flexDirection: "column",
      padding: "24px 24px 22px",
      borderLeft: `1px solid ${line}`,
      background: soft,
    },
    summaryHeading: { margin: "0 0 16px", fontSize: 15, fontWeight: 650, color: text },
    lines: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 14 },
    lineRow: { display: "flex", flexDirection: "column", gap: 2, animation: "$lineIn 180ms ease-out", [reduce]: { animation: "none" } },
    lineTop: { display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14, fontWeight: 600, color: text },
    lineValue: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    lineDetail: { fontSize: 12.5, color: muted, fontVariantNumeric: "tabular-nums" },
    total: { display: "flex", flexDirection: "column", gap: 2, paddingTop: 14, marginTop: 16, borderTop: `1px dashed ${dark ? "rgba(255,255,255,0.2)" : "#A1A1AA"}`, transition: "opacity 140ms ease", [reduce]: { transition: "none" } },
    totalCaption: { display: "flex", alignItems: "center", gap: 4, fontSize: 13, color: muted },
    totalAmount: {
      fontSize: 32,
      lineHeight: 1.15,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: text,
      fontVariantNumeric: "tabular-nums",
      display: "inline-block",
      animation: "$tick 180ms ease-out",
      [reduce]: { animation: "none" },
    },
    totalHint: { fontSize: 12, color: muted },
    quoteRows: { display: "flex", flexDirection: "column", gap: 8, marginTop: 16, transition: "opacity 140ms ease", [reduce]: { transition: "none" } },
    quoteRowsBusy: { opacity: 0.45 },
    barHint: { margin: "-6px 0 0", fontSize: 12, lineHeight: 1.4, color: muted },
    quoteRow: { display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, color: muted },
    quoteWaiting: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: muted, marginTop: 16 },
    quoteNote: { margin: "6px 0 0", fontSize: 12.5, lineHeight: 1.45, color: muted },
    quoteNothing: { margin: "16px 0 0", fontSize: 13, color: muted },
    quoteAlert: { display: "flex", flexDirection: "column", gap: 6, marginTop: 16, [compact]: { marginTop: 0 } },
    actionBox: { display: "flex", flexDirection: "column", gap: 10, marginTop: 16 },
    cta: {
      height: 48,
      width: "100%",
      borderRadius: 12,
      border: "none",
      background: "#3D5CFD",
      color: "#fff",
      fontFamily: "inherit",
      fontSize: 15,
      fontWeight: 700,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      fontVariantNumeric: "tabular-nums",
      transition: "background 120ms ease, transform 120ms ease",
      "&:hover:not(:disabled)": { background: "#4C69FF" },
      "&:active:not(:disabled)": { transform: "translateY(1px)" },
      "&:disabled": { opacity: 0.5, cursor: "default" },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 1px ${paper}, 0 0 0 2px ${dark ? "#fff" : "#09090B"}` },
      [reduce]: { transition: "none", "&:active:not(:disabled)": { transform: "none" } },
    },
    ctaNote: { margin: 0, fontSize: 12.5, lineHeight: 1.45, color: muted, textAlign: "center" },
    trust: { listStyle: "none", margin: "16px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 10 },
    trustItem: { display: "flex", alignItems: "flex-start", gap: 9, fontSize: 12.5, lineHeight: 1.45, color: muted, "& svg": { width: 15, height: 15, flexShrink: 0, marginTop: 1, color: faint } },
    trustInline: { margin: 0, paddingTop: 16, borderTop: `1px solid ${line}` },

    // Abaixo de 960 px: o total e o botão fixos embaixo; os itens abrem por cima, se a pessoa quiser.
    bar: {
      flexShrink: 0,
      borderTop: `1px solid ${line}`,
      background: dark ? "#19191C" : "#FAFAFA",
      padding: "10px 22px 14px",
      paddingBottom: "calc(14px + env(safe-area-inset-bottom))",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      [phone]: { padding: "10px 16px 12px", paddingBottom: "calc(12px + env(safe-area-inset-bottom))" },
    },
    barDetails: {
      maxHeight: "42vh",
      overflowY: "auto",
      paddingBottom: 12,
      borderBottom: `1px solid ${line}`,
      animation: "$lineIn 160ms ease-out",
      [reduce]: { animation: "none" },
    },
    barToggle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      width: "100%",
      border: "none",
      background: "transparent",
      padding: 0,
      fontFamily: "inherit",
      color: text,
      cursor: "pointer",
      textAlign: "left",
      borderRadius: 6,
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    barCaption: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: muted, "& svg": { width: 16, height: 16, transition: "transform 160ms ease", [reduce]: { transition: "none" } } },
    barChevronOpen: { "& svg": { transform: "rotate(180deg)" } },
    barAmount: { fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", color: text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },

    // A etapa 2: dados para a cobrança e forma de pagamento.
    billingCard: { display: "flex", flexDirection: "column", gap: 3, fontSize: 13, lineHeight: 1.5, color: muted, overflowWrap: "anywhere" },
    billingName: { fontSize: 14, fontWeight: 600, color: text },
    billingIntro: { margin: "-6px 0 0", fontSize: 13, lineHeight: 1.5, color: muted },
    busyRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: muted },
    emailBox: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: "14px 16px",
      borderRadius: 12,
      background: c.warnBg,
      border: `1px solid ${c.warnBorder}`,
    },
    emailTitle: { display: "flex", alignItems: "center", gap: 8, margin: 0, fontSize: 14, fontWeight: 650, color: text, "& svg": { width: 16, height: 16, flexShrink: 0, color: c.warn } },
    emailText: { margin: 0, fontSize: 13, lineHeight: 1.5, color: text, overflowWrap: "anywhere" },
    emailActions: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 },
    emailNotice: { margin: 0, fontSize: 12.5, lineHeight: 1.45, color: muted },
    emailError: { margin: 0, fontSize: 12.5, lineHeight: 1.45, color: c.bad },

    methods: { display: "flex", flexWrap: "wrap", gap: 8 },
    method: {
      flex: "1 1 0",
      minWidth: 140,
      height: 44,
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.12)" : "#D4D4D8"}`,
      background: "transparent",
      color: muted,
      fontFamily: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "0 12px",
      whiteSpace: "nowrap",
      transition: "background 140ms ease, border-color 140ms ease, color 140ms ease",
      "& svg": { width: 16, height: 16, flexShrink: 0 },
      "&:hover": { background: soft, color: text },
      "&:focus-visible": { outline: "none", boxShadow: focus },
      [reduce]: { transition: "none" },
      [phone]: { minWidth: 110 },
    },
    methodSelected: { background: raised, borderColor: strong, color: text, "&:hover": { background: raised } },
    methodDisabled: { opacity: 0.45, cursor: "default", "&:hover": { background: "transparent", color: muted } },
    note: { margin: 0, fontSize: 12.5, lineHeight: 1.5, color: muted },

    cardGrid: { display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: "14px 12px", marginTop: 2 },
    cardNumberCell: { gridColumn: "span 7", [phone]: { gridColumn: "span 12" } },
    cardHolderCell: { gridColumn: "span 5", [phone]: { gridColumn: "span 12" } },
    cardExpiryCell: { gridColumn: "span 3", [phone]: { gridColumn: "span 6" } },
    cardCvvCell: { gridColumn: "span 3", [phone]: { gridColumn: "span 6" } },
    cardNote: {
      gridColumn: "span 6",
      // Na altura dos campos ao lado (abaixo dos rótulos), também quando eles mostram um erro.
      alignSelf: "start",
      paddingTop: 28,
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      fontSize: 12.5,
      lineHeight: 1.45,
      color: muted,
      "& svg": { width: 15, height: 15, flexShrink: 0, marginTop: 1, color: faint },
      [phone]: { gridColumn: "span 12", paddingTop: 0 },
    },
    // O ⓘ não pode aumentar a linha do rótulo: os campos lado a lado ficam alinhados.
    labelRow: { display: "flex", alignItems: "center", gap: 4, height: 18, "& button": { marginTop: 0, marginBottom: 0 } },
    inputWrap: { position: "relative" },
    cardBrand: { position: "absolute", right: 12, top: 0, height: 42, display: "inline-flex", alignItems: "center", fontSize: 12.5, fontWeight: 600, color: muted, pointerEvents: "none" },
    inputWithBrand: { paddingRight: 88 },

    // Os campos: os mesmos no cartão e nos dados para a cobrança.
    grid: { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "14px 12px", [phone]: { gridTemplateColumns: "1fr" } },
    span2: { gridColumn: "span 2", [phone]: { gridColumn: "auto" } },
    span3: { gridColumn: "span 3", [phone]: { gridColumn: "auto" } },
    span4: { gridColumn: "span 4", [phone]: { gridColumn: "auto" } },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    label: { fontSize: 12.5, lineHeight: "18px", fontWeight: 600, color: text },
    optional: { fontWeight: 400, color: muted },
    zipBusy: { marginLeft: 6, verticalAlign: "-1px" },
    input: fieldBase,
    inputError: { borderColor: c.bad, "&:focus": { borderColor: c.bad } },
    fieldError: { fontSize: 12, lineHeight: 1.4, color: c.bad },
    // "&&": as regras do select padrão empatariam. Mesma altura, borda e fundo dos campos.
    select: selectBase,
    selectError: { "&& .MuiOutlinedInput-notchedOutline": { borderColor: `${c.bad} !important` } },

    "@keyframes stepIn": { from: { opacity: 0, transform: "translateX(8px)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes lineIn": { from: { opacity: 0, transform: "translateY(-3px)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes tick": { from: { opacity: 0.45, transform: "translateY(2px)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes pop": { from: { transform: "scale(0.6)", opacity: 0 }, to: { transform: "scale(1)", opacity: 1 } },
  };
});

export default useCheckoutStyles;
